/**
 * LFY Theme — color engine. One brand `primary` hex (preset or custom) derives
 * every `--p-*` CSS custom property on the page root.
 *
 * Two rules not to "simplify" away:
 *   1. Borders/badges use SOLID blended colors, never alpha — an alpha border
 *      vanishes on a white brand color. Borders floor to grey when too pale;
 *      badges fall back to ink when the brand can't carry white text.
 *   2. Only accent fills use alpha (`--p-primary-tint/-soft`, header treatments) —
 *      they sit on white, so transparency is safe.
 */

export type ThemeTokens = { [cssVar: string]: string };

export type Treatment = "bold" | "wash" | "soft" | "gradient" | "white";

export interface Palette {
  id: string;
  name: string;
  primary: string;
  heroFrom: string;
  heroTo: string;
}

// --- 1. Color helper functions (per spec) ------------------------------------

/** Expand a hex string to 6 digits: strip '#', expand #abc -> aabbcc, drop alpha. */
function normalizeHex(hex: string): string {
  const m = (hex || "").replace("#", "");
  if (!m) return "000000";
  if (m.length === 3) {
    return m
      .split("")
      .map(c => c + c)
      .join("");
  }
  return m.slice(0, 6);
}

export function hexToRgba(hex: string, a: number): string {
  const v = normalizeHex(hex);
  return `rgba(${parseInt(v.slice(0, 2), 16)},${parseInt(
    v.slice(2, 4),
    16
  )},${parseInt(v.slice(4, 6), 16)},${a})`;
}

export function hexToRgb(hex: string): [number, number, number] {
  const v = normalizeHex(hex);
  return [
    parseInt(v.slice(0, 2), 16),
    parseInt(v.slice(2, 4), 16),
    parseInt(v.slice(4, 6), 16)
  ];
}

export function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

/** Blend a color toward white by ratio t (0 = original, 1 = white). Solid hex. */
export function mixWithWhite(hex: string, t: number): string {
  const [r, g, b] = hexToRgb(hex);
  const mix = (c: number) => Math.round(c + (255 - c) * t);
  return rgbToHex(mix(r), mix(g), mix(b));
}

/** Perceived brightness 0..1 (used to decide fallbacks). */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** Blend color a toward color b by ratio t. Solid hex. */
export function mixHex(a: string, b: string, t: number): string {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  const m = (x: number, y: number) => Math.round(x + (y - x) * t);
  return rgbToHex(m(r1, r2), m(g1, g2), m(b1, b2));
}

/**
 * BORDER: tinted hairline that stays visible even when the brand color is
 * white/near-white. Blend toward white by t; if the result is too pale to read on
 * white, blend toward neutral grey (#D7D8DE) so the line never vanishes.
 */
export function softBorder(hex: string, t: number): string {
  const tinted = mixWithWhite(hex, t);
  const lum = luminance(tinted);
  if (lum > 0.92) {
    const k = Math.min(1, (lum - 0.92) / 0.08); // 0 at lum .92 -> 1 at lum 1.0
    return mixHex(tinted, "#D7D8DE", k);
  }
  return tinted;
}

/**
 * BADGE: notification badge carries WHITE text. If the brand color is too light to
 * carry white text, fall back to ink (#1E1E1E) so the badge stays legible.
 */
export function badgeColor(hex: string): string {
  return luminance(hex) > 0.62 ? "#1E1E1E" : hex;
}

/**
 * ON-PRIMARY: auto-contrast text/icon color on the brand fill (--p-primary).
 * Mirrors miscUtils.getAutoFontColor and themeTokens.onColor — presets always get
 * white; custom colors use WCAG luminance with a white-biased 0.5 threshold (so
 * mid-tone blues/purples get white, light brands like #FFD93D keep black).
 * The preset branch keeps --p-on-primary agreeing with the icon color; without it
 * a dark-side-of-0.5 preset yields a white icon but a black label.
 */
export function onColor(hex: string, branding?: any): string {
  if (branding && branding.name && String(branding.name).toLowerCase() !== "custom") {
    return "#ffffff";
  }
  const [r, g, b] = hexToRgb(hex);
  const lin = (c: number) => {
    const n = c / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.5 ? "#000000" : "#ffffff";
}

// --- 6. Preset palettes ------------------------------------------------------

export const PALETTES: Palette[] = [
  { id: "default", name: "Default", primary: "#5B5C65", heroFrom: "#F3F4F6", heroTo: "#D5D7DC" },
  { id: "group-blue", name: "Group Blue", primary: "#365DE6", heroFrom: "#E8B5BD", heroTo: "#D97488" },
  { id: "indigo", name: "Indigo", primary: "#4338CA", heroFrom: "#FFDFA0", heroTo: "#F2A23B" },
  { id: "sage", name: "Sage", primary: "#2D7D5F", heroFrom: "#F2D3BD", heroTo: "#E8A87C" },
  { id: "slate", name: "Slate", primary: "#475569", heroFrom: "#E8B5BD", heroTo: "#D97488" },
  { id: "ink", name: "Ink", primary: "#1E1E1E", heroFrom: "#FFD993", heroTo: "#F2A23B" },
  { id: "warm", name: "Warm", primary: "#B85C2C", heroFrom: "#E8D9B5", heroTo: "#C9A86A" },
  { id: "rose", name: "Rose", primary: "#BE3F5F", heroFrom: "#F2D4DC", heroTo: "#D97488" },
  { id: "forest", name: "Forest", primary: "#1F4E3F", heroFrom: "#F0E1B5", heroTo: "#C99A4A" }
];

/** Custom color: hero stops are derived from the single picked color. */
export function deriveCustom(customColor: string): Palette {
  return {
    id: "custom",
    name: "Custom",
    primary: customColor,
    heroFrom: mixWithWhite(customColor, 0.65),
    heroTo: mixWithWhite(customColor, 0.3)
  };
}

// --- 2. Token derivation -----------------------------------------------------

/** Brand-accent tokens — same for every theme. */
function paletteTokens(p: Palette, branding?: any): ThemeTokens {
  return {
    "--p-primary": p.primary,
    "--p-on-primary": onColor(p.primary, branding), // auto-contrast text on the brand fill
    "--p-badge": badgeColor(p.primary), // SOLID — never alpha
    "--p-primary-tint": hexToRgba(p.primary, 0.1), // 10% alpha
    "--p-primary-soft": hexToRgba(p.primary, 0.14), // 14% alpha
    "--p-primary-hover": hexToRgba(p.primary, 0.2), // 20% alpha — sidebar hover fill
    "--p-hero-from": p.heroFrom,
    "--p-hero-to": p.heroTo
  };
}

/** Surface/border tokens — branched on neutral vs. brand-tinted. */
function surfaceTokens(primary: string, isNeutral: boolean): ThemeTokens {
  if (isNeutral) {
    return {
      "--p-surface": "#FFFFFF",
      "--p-surface-tint": "#F7F8F9",
      "--p-border": "#EBECF0", // fixed neutral grey
      "--p-canvas": "#F7F8F9",
      "--p-card": "#FFFFFF",
      "--p-card-border": "#E7E9EE", // fixed neutral grey
      "--p-primary-tint": hexToRgba(primary, 0.1), // 10% alpha
      "--p-primary-soft": hexToRgba(primary, 0.14) // 14% alpha
    };
  }
  return {
    "--p-surface": mixWithWhite(primary, 0.96), // solid, 96% toward white
    "--p-surface-tint": mixWithWhite(primary, 0.91), // solid, 91% toward white
    "--p-border": softBorder(primary, 0.82), // SOLID hairline w/ grey floor
    "--p-canvas": mixWithWhite(primary, 0.9), // solid, 90% toward white
    "--p-card": "#FFFFFF",
    "--p-card-border": softBorder(primary, 0.86), // SOLID hairline w/ grey floor
    "--p-primary-tint": hexToRgba(primary, 0.18), // 18% alpha
    "--p-primary-soft": hexToRgba(primary, 0.22) // 22% alpha
  };
}

/**
 * Brand-tint family (`--p-brand-tint/-border/-strong`) for soft chips, dividers,
 * and small badges. Written on documentElement (not just the legacy `#master`
 * writer) so consumers don't flash grey while `#master` transiently loses its
 * tokens on client-side nav. Values MUST match buildThemedTokens in themeTokens.ts.
 * Themed communities only — neutral keeps the SCSS fallbacks. `--p-brand` is
 * deliberately omitted: it's overloaded as both button bg and text color, so a
 * global value would put white text on white — needs per-component cleanup.
 */
function brandTokens(primary: string): ThemeTokens {
  return {
    "--p-brand-tint": mixWithWhite(primary, 0.92),
    "--p-brand-border": mixWithWhite(primary, 0.72),
    "--p-brand-strong": mixHex(primary, "#000000", 0.18)
  };
}

/** Header "treatment" fill behind a section header (§3). */
export function treatmentFill(primary: string, treatment: Treatment): string {
  switch (treatment) {
    case "bold":
      return primary;
    case "wash":
      return hexToRgba(primary, 0.22);
    case "soft":
      return hexToRgba(primary, 0.08);
    case "gradient":
      return `linear-gradient(180deg, ${primary} 0%, ${mixWithWhite(
        primary,
        0.55
      )} 55%, ${mixWithWhite(primary, 0.88)} 100%)`;
    case "white":
    default:
      return "#fff";
  }
}

/**
 * Constant overlays, independent of brand color. Names match what the SCSS already
 * consumes (`--p-card-shadow` is read in ~51 places), so every card gets the shadow.
 */
export const CONSTANT_OVERLAYS: ThemeTokens = {
  "--p-scrollbar-thumb": "rgba(0,0,0,0.15)",
  "--p-scrollbar-thumb-hover": "rgba(0,0,0,0.32)",
  "--p-card-shadow": "0 0 2px rgba(16,40,30,0.05), 0 0 8px rgba(16,40,30,0.06)",
  "--p-shadow-elevated": "0 8px 32px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)",
  "--p-favorite-chip-bg": "rgba(255,255,255,0.92)"
};

// --- Input bridge ------------------------------------------------------------

/**
 * Resolve theme input from a community's `custom_branding`:
 *   - numeric `paletteIndex`         -> that preset (neutral === index 0)
 *   - `primaryColor` with no index   -> custom color (hero derived; never neutral)
 *   - nothing                        -> Default preset (the only neutral theme)
 *
 * White is a valid theme here — this layer ignores the legacy
 * `miscUtils.customGroupThemeSet` white-as-"off" sentinel.
 */
export interface ThemeInput {
  palette: Palette;
  paletteIndex: number;
  isCustom: boolean;
  isNeutral: boolean;
}

export function resolveThemeInput(customBranding: any): ThemeInput {
  const cb = customBranding || {};

  if (typeof cb.paletteIndex === "number" && PALETTES[cb.paletteIndex]) {
    const paletteIndex = cb.paletteIndex;
    return {
      palette: PALETTES[paletteIndex],
      paletteIndex,
      isCustom: false,
      isNeutral: paletteIndex === 0
    };
  }

  if (cb.primaryColor) {
    return {
      palette: deriveCustom(cb.primaryColor),
      paletteIndex: -1,
      isCustom: true,
      isNeutral: false
    };
  }

  return {
    palette: PALETTES[0],
    paletteIndex: 0,
    isCustom: false,
    isNeutral: true
  };
}

/** Assemble the full `--p-*` token map (accent + surface + constant overlays). */
export function getThemeTokens(customBranding: any): ThemeTokens {
  const { palette, isNeutral } = resolveThemeInput(customBranding);
  return {
    ...paletteTokens(palette, customBranding),
    ...surfaceTokens(palette.primary, isNeutral),
    // Brand-tint family for themed communities only; neutral keeps the SCSS fallbacks.
    ...(isNeutral ? {} : brandTokens(palette.primary)),
    ...CONSTANT_OVERLAYS
  };
}

/** Write tokens onto a root element. Client-only (SSR-guarded — see `npm run ssr`). */
export function applyThemeTokens(tokens: ThemeTokens, root?: HTMLElement): void {
  if (typeof document === "undefined") return;
  const el = root || document.documentElement;
  Object.keys(tokens).forEach(key => el.style.setProperty(key, tokens[key]));
}
